/*
 * predictive-search.js — <predictive-search> using /search/suggest.json.
 * Progressive enhancement: the inner <form action="/search"> works without JS.
 */
(function () {
  'use strict';
  const JF = (window.JF = window.JF || {});

  class PredictiveSearch extends HTMLElement {
    connectedCallback() {
      this.input = this.querySelector('input[type="search"]');
      this.results = this.querySelector('[data-predictive-results]');
      this.status = this.querySelector('[data-predictive-status]');
      this.form = this.querySelector('form');
      if (!this.input || !this.results) return;

      this.cache = {};
      this.onInput = JF.debounce(this.search.bind(this), 250);
      this.input.addEventListener('input', this.onInput);
      this.input.addEventListener('focus', () => {
        if (this.input.value.length >= 2 && this.results.innerHTML.trim()) this.open();
      });
      this.addEventListener('keydown', this.onKeydown.bind(this));
      document.addEventListener('click', (e) => {
        if (!this.contains(e.target)) this.close();
      });
    }

    search() {
      const q = this.input.value.trim();
      if (q.length < 2) {
        this.close();
        return;
      }
      if (this.cache[q]) {
        this.render(this.cache[q], q);
        return;
      }
      this.setAttribute('loading', '');
      const params = new URLSearchParams({
        q: q,
        'resources[type]': 'product,collection,article,page,query',
        'resources[limit]': '6',
        'resources[options][unavailable_products]': 'last',
      });
      fetch('/search/suggest.json?' + params.toString(), { headers: { Accept: 'application/json' } })
        .then((r) => r.json())
        .then((data) => {
          this.cache[q] = data.resources.results;
          this.render(data.resources.results, q);
        })
        .catch(() => {})
        .finally(() => this.removeAttribute('loading'));
    }

    render(results, q) {
      const products = results.products || [];
      const queries = results.queries || [];
      const collections = results.collections || [];
      const articles = results.articles || [];
      let html = '';

      if (queries.length) {
        html += '<div class="predictive__group"><ul class="predictive__list" role="list">';
        queries.slice(0, 4).forEach((item) => {
          html += `<li><a class="predictive__query" href="${item.url}" role="option">${item.styled_text || item.text}</a></li>`;
        });
        html += '</ul></div>';
      }

      if (products.length) {
        html += '<div class="predictive__group"><p class="predictive__heading">Produits</p><ul class="predictive__list predictive__products" role="list">';
        products.forEach((p) => {
          const img = p.featured_image && p.featured_image.url
            ? `<img src="${p.featured_image.url}" alt="" width="56" height="56" loading="lazy">`
            : '<span class="predictive__thumb-empty"></span>';
          html += `<li><a class="predictive__product" href="${p.url}" role="option">
            <span class="predictive__thumb">${img}</span>
            <span class="predictive__product-info"><span class="predictive__product-title">${p.title}</span>
            ${p.price ? `<span class="predictive__product-price">${p.price}</span>` : ''}</span></a></li>`;
        });
        html += '</ul></div>';
      }

      if (collections.length || articles.length) {
        html += '<div class="predictive__group"><ul class="predictive__list" role="list">';
        collections.slice(0, 3).forEach((c) => {
          html += `<li><a class="predictive__link" href="${c.url}" role="option">${c.title}</a></li>`;
        });
        articles.slice(0, 3).forEach((a) => {
          html += `<li><a class="predictive__link" href="${a.url}" role="option">${a.title}</a></li>`;
        });
        html += '</ul></div>';
      }

      if (!products.length && !queries.length && !collections.length && !articles.length) {
        html = `<p class="predictive__empty">Aucune suggestion pour « ${q} ».</p>`;
      } else if (this.form) {
        const action = this.form.getAttribute('action') || '/search';
        html += `<a class="predictive__all" href="${action}?q=${encodeURIComponent(q)}">Voir tous les résultats</a>`;
      }

      this.results.innerHTML = html;
      this.open();
      if (this.status) {
        const count = products.length + queries.length + collections.length + articles.length;
        this.status.textContent = count + ' suggestions';
      }
    }

    onKeydown(e) {
      if (e.key === 'Escape') {
        this.close();
        this.input.focus();
        return;
      }
      if (e.key !== 'ArrowDown' && e.key !== 'ArrowUp') return;
      const options = Array.from(this.results.querySelectorAll('[role="option"]'));
      if (!options.length) return;
      e.preventDefault();
      const current = options.indexOf(document.activeElement);
      let next = e.key === 'ArrowDown' ? current + 1 : current - 1;
      if (next < 0) next = options.length - 1;
      if (next >= options.length) next = 0;
      options[next].focus();
    }

    open() {
      this.setAttribute('open', '');
      this.input.setAttribute('aria-expanded', 'true');
    }
    close() {
      this.removeAttribute('open');
      this.input.setAttribute('aria-expanded', 'false');
    }
  }
  if (!customElements.get('predictive-search')) customElements.define('predictive-search', PredictiveSearch);
})();
